// Every key the app listens for, in one table, so a user can rebind any of
// them (hooks/useShortcuts.tsx persists the overrides in user preferences).
//
// A combo is a lowercase string: optional "mod+" (Cmd on a Mac, Ctrl
// elsewhere), "alt+" and "shift+", then the key itself — "mod+k", "/", "\\",
// "arrowdown", "j". Shift is only part of a combo for keys that don't produce
// a character of their own; "?" is already the shifted "/", so it is written
// as "?" rather than "shift+/".

export type ShortcutId =
  | "globalSearch"
  | "filterPage"
  | "focusRegion"
  | "shortcutHelp"
  | "moveDown"
  | "moveUp"
  | "moveFirst"
  | "moveLast"
  | "expand"
  | "collapse"
  | "open"
  | "openModal"
  | "toggleSelect"
  | "bulkArchive"
  | "bulkDelete"
  | "newTask"
  | "dismiss";

export type Bindings = Record<ShortcutId, string[]>;

export const SHORTCUTS: { id: ShortcutId; label: string; description: string; group: string; defaults: string[] }[] = [
  { id: "globalSearch", label: "Search everywhere", description: "Open the search palette.", group: "Getting around", defaults: ["mod+k"] },
  { id: "filterPage", label: "Filter this page", description: "Jump to the filter box on the page or the modal in front of it.", group: "Getting around", defaults: ["/"] },
  { id: "focusRegion", label: "Sidebar / page", description: "Move between the sidebar and the page, so the arrows drive one or the other.", group: "Getting around", defaults: ["\\"] },
  { id: "shortcutHelp", label: "Keyboard shortcuts", description: "Show this list.", group: "Getting around", defaults: ["?"] },
  { id: "moveDown", label: "Down", description: "Next row, next sidebar entry, next match.", group: "Moving", defaults: ["arrowdown"] },
  { id: "moveUp", label: "Up", description: "Previous row, sidebar entry or match.", group: "Moving", defaults: ["arrowup"] },
  { id: "moveFirst", label: "First", description: "Jump to the top of the list.", group: "Moving", defaults: ["home"] },
  { id: "moveLast", label: "Last", description: "Jump to the bottom of the list.", group: "Moving", defaults: ["end"] },
  { id: "expand", label: "Expand", description: "Open a row that holds children.", group: "Moving", defaults: ["arrowright"] },
  { id: "collapse", label: "Collapse", description: "Close a row that holds children.", group: "Moving", defaults: ["arrowleft"] },
  { id: "open", label: "Open", description: "Open the row, entry or match under the cursor.", group: "Acting", defaults: ["enter"] },
  { id: "openModal", label: "Open without leaving", description: "Open the row's dialog — the edit or peek panel — instead of following its link.", group: "Acting", defaults: ["mod+enter"] },
  { id: "toggleSelect", label: "Check / uncheck", description: "Toggle selection on the focused row.", group: "Acting", defaults: [" "] },
  { id: "bulkArchive", label: "Archive selected", description: "Archive selected items (Gmail style).", group: "Bulk actions", defaults: ["e"] },
  { id: "bulkDelete", label: "Delete selected", description: "Delete selected items permanently.", group: "Bulk actions", defaults: ["#"] },
  { id: "newTask", label: "New task", description: "Create a new task from anywhere.", group: "Acting", defaults: ["mod+n"] },
  { id: "dismiss", label: "Dismiss", description: "Clear the filter, drop the cursor, close the palette.", group: "Acting", defaults: ["escape"] },
];

export const DEFAULT_BINDINGS: Bindings = Object.fromEntries(
  SHORTCUTS.map((s) => [s.id, s.defaults])
) as Bindings;

export const PREF_KEY = "shortcuts";

const IS_MAC = typeof navigator !== "undefined" && /mac/i.test(navigator.platform || navigator.userAgent);

export function eventCombo(e: KeyboardEvent | React.KeyboardEvent): string | null {
  const key = e.key;
  if (!key || ["Meta", "Control", "Alt", "Shift"].includes(key)) return null;
  const printable = key.length === 1;
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push("mod");
  if (e.altKey) parts.push("alt");
  // A printable key already carries its shifted identity ("?" not "shift+/").
  if (e.shiftKey && !printable) parts.push("shift");
  parts.push(key.toLowerCase());
  return parts.join("+");
}

export function matches(e: KeyboardEvent | React.KeyboardEvent, combos: string[] | undefined): boolean {
  const combo = eventCombo(e);
  return !!combo && !!combos?.includes(combo);
}

// A bare character — the kind that would otherwise land in a text field. The
// filter box stays typeable, so it only honours combos that aren't these.
export function isBareCharacter(combo: string): boolean {
  return combo.length === 1;
}

export function bindingsAreTypeable(combos: string[] | undefined): boolean {
  return !!combos?.some((c) => !isBareCharacter(c));
}

export function matchesTypeable(e: KeyboardEvent | React.KeyboardEvent, combos: string[] | undefined): boolean {
  const combo = eventCombo(e);
  return !!combo && !isBareCharacter(combo) && !!combos?.includes(combo);
}

// Boxes that exist to steer the app rather than to hold content: the global
// search field, the palette's own field and the page filter. A bare key that
// means "take me elsewhere" still means that when the cursor is sitting in one
// of these, which is where it usually is when the user wants to leave.
const NAVIGATION_FIELDS = [
  'input[aria-label="Global search"]',
  'input[aria-label="Search clients, projects, leads, tasks"]',
  'input[aria-label="Filter this page"]',
  'input[type="search"]',
].join(",");

export function isNavigationField(el: Element | null | undefined): boolean {
  return !!el?.matches(NAVIGATION_FIELDS);
}

const KEY_LABELS: Record<string, string> = {
  arrowdown: "Down", arrowup: "Up", arrowleft: "Left", arrowright: "Right",
  enter: "Enter", escape: "Esc", home: "Home", end: "End",
  " ": "Space", pagedown: "PgDn", pageup: "PgUp", tab: "Tab", backspace: "Backspace",
};

export function formatCombo(combo: string): string {
  return combo
    .split("+")
    .map((part) => {
      if (part === "mod") return IS_MAC ? "⌘" : "Ctrl";
      if (part === "alt") return IS_MAC ? "⌥" : "Alt";
      if (part === "shift") return IS_MAC ? "⇧" : "Shift";
      return KEY_LABELS[part] ?? (part.length === 1 ? part.toUpperCase() : part);
    })
    .join(IS_MAC ? "" : "+");
}

export function mergeBindings(stored: unknown): Bindings {
  const merged = { ...DEFAULT_BINDINGS };
  if (stored && typeof stored === "object") {
    for (const { id } of SHORTCUTS) {
      const value = (stored as Record<string, unknown>)[id];
      if (Array.isArray(value) && value.every((v) => typeof v === "string" && v)) {
        merged[id] = value as string[];
      }
    }
  }
  return merged;
}

// Which other actions already answer to this combo. Two actions on one key is
// the one way a rebind can quietly break something else, so the dialog says so
// before it saves.
export function conflictsWith(bindings: Bindings, combo: string, self: ShortcutId): ShortcutId[] {
  return (Object.keys(bindings) as ShortcutId[]).filter((id) => id !== self && bindings[id].includes(combo));
}
